/** biome-ignore-all lint/style/useNamingConvention: GTM event format requirement*/

import { findOnetrustCookiePerformance } from '@/app/_lib/services/oneTrust/cookies';
import type { ClickTarget, ModuleType, TrackContentCardClickParams } from '@/app/_lib/types/ContentCardTracking';
import type { PageViewDataLayerObject } from '@/app/_lib/types/Gtm';
import { getPrimarySection } from '@/app/_lib/utils/utils';
import type { Article } from '@/app_lib/types/Article';
import type { ArticlePreview, ArticlePreviewFeatured } from '@/app_lib/types/ArticlePreview';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';
import type { VariantProps } from '@/main/lib/providers/OptimizelyProvider';

// PageType detection based on cmsCategoryCD from dataLayer
const getPageType = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }
  const pageViewData = (window as unknown as { dataLayer?: PageViewDataLayerObject[] })?.dataLayer?.find(
    (item: PageViewDataLayerObject) => item?.event === 'fortune.historyChange',
  );
  return pageViewData?.cmsCategoryCD?.toLowerCase() || '';
};

// Get user login state from dataLayer (converts loginStatusCD to boolean logged_in)
const getUserLoggedIn = (): boolean | undefined => {
  if (typeof window === 'undefined') {
    return;
  }
  const userDataEntry = (window as unknown as { dataLayer?: Record<string, unknown>[] })?.dataLayer
    ?.slice()
    .reverse()
    .find(
      (item: Record<string, unknown>) => item && typeof item === 'object' && item !== null && 'loginStatusCD' in item,
    );

  if (!userDataEntry?.loginStatusCD) {
    return;
  }
  return userDataEntry.loginStatusCD === 'logged-in';
};

/**
 * Tracks content card click events to GTM with comprehensive event data
 * @param params - The tracking parameters
 */
export const trackContentCardClick = ({
  article,
  moduleType,
  cardPosition,
  clickTarget,
  destinationUrl,
}: TrackContentCardClickParams): void => {
  // Check OneTrust consent for performance tracking (C0002:1 && C0003:1)
  if (!findOnetrustCookiePerformance()) {
    return;
  }

  // Get current page data
  const sourceUrl = typeof window !== 'undefined' ? window.location.href : '';
  const pageType = getPageType();

  const contentId = article.databaseId?.toString() ?? '';

  const primarySection = getPrimarySection(article.primarySectionId, article.sections);

  const loggedIn = getUserLoggedIn();

  const eventPayload: Record<string, unknown> = {
    card_position: cardPosition,
    click_target: clickTarget,
    content_id: contentId,
    destination_url: destinationUrl,
    event: 'content_card_click',
    module_type: moduleType,
    page_type: pageType,
    primary_section: primarySection?.name ?? '',
    source_url: sourceUrl,
    timestamp: new Date().toISOString(),
  };

  if (loggedIn !== undefined) {
    eventPayload.logged_in = loggedIn;
  }

  if (typeof window !== 'undefined') {
    window.dataLayer?.push(eventPayload);
  }
};

/**
 * Generates tracking data for content card clicks.
 * Callers should normalize the article via getExtendedArticle before passing it here.
 */
export function generateContentCardTrackingData(params: {
  clickTarget: ClickTarget;
  article: ArticlePreview | ArticlePreviewFeatured | Article;
  moduleType: ModuleType;
  cardPosition: number;
}): TrackContentCardClickParams {
  const { clickTarget, article, moduleType, cardPosition } = params;

  return {
    article: article as ArticlePreview,
    cardPosition,
    clickTarget,
    destinationUrl: article.url,
    moduleType,
  };
}

export function useContentCardTrackingData() {
  const { userFlags } = useOptimizely();
  const isEnabled = (userFlags.contentCardTrackingData as VariantProps)?.enabled ?? false;

  return (params: Parameters<typeof generateContentCardTrackingData>[0]): TrackContentCardClickParams | undefined => {
    if (!isEnabled) {
      return;
    }
    return generateContentCardTrackingData(params);
  };
}
